import React, { useEffect, useState, useCallback } from "react";
import { toast } from "sonner";
import { Clapperboard, RefreshCw, Loader2, Play } from "lucide-react";
import api, { formatApiErrorDetail } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { invalidateMillieClips } from "./MascotBubble";

const API_BASE = process.env.REACT_APP_BACKEND_URL;
const CLIPS = [{ key: "idle", label: "Idle (Home)" }, { key: "celebrate", label: "Celebrate" }, { key: "thinking", label: "Thinking" }, { key: "cooking", label: "Cooking (Cook Mode)" }];

export default function MillieStudio() {
  const { user } = useAuth();
  const [clips, setClips] = useState({});
  const load = useCallback(async () => { try { const { data } = await api.get("/millie/clips"); setClips(data); invalidateMillieClips(); } catch {} }, []);
  useEffect(() => { load(); const t = setInterval(() => { if (Object.values(clips).some((c) => c.status === "rendering")) load(); }, 10000); return () => clearInterval(t); }, [load, clips]);
  if (user?.email !== "nikkihulcombe@gmail.com" && user?.role !== "admin") return null;

  const render = async (clip, force) => {
    try { const { data } = await api.post("/millie/clips/generate", { clip, force }); toast(data.status === "rendering" ? `Rendering ${clip} — about 2–3 minutes` : `${clip} is already ready`); load(); }
    catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); }
  };

  return (
    <div className="bg-white rounded-3xl border-2 border-[#FFF3F0] shadow-soft p-4" data-testid="millie-studio">
      <div className="flex items-center gap-3">
        <span className="w-10 h-10 rounded-full bg-[#E7E0F3] flex items-center justify-center text-purple-500"><Clapperboard size={18} strokeWidth={2.5} /></span>
        <div className="flex-1">
          <p className="font-heading font-semibold text-sorted-ink">Millie Studio</p>
          <p className="text-xs text-sorted-slate">Living clips rendered from Millie's artwork (fal.ai Kling · ~A$0.55 per 5s clip on your Universal Key)</p>
        </div>
      </div>
      <div className="mt-4 grid grid-cols-3 gap-2">
        {CLIPS.map((c) => {
          const st = clips[c.key];
          return (
            <div key={c.key} className="rounded-2xl border-2 border-[#FFF3F0] p-2 text-center" data-testid={`clip-${c.key}`}>
              <div className="aspect-[3/4] rounded-xl overflow-hidden bg-[#FFF6F1] flex items-center justify-center">
                {st?.status === "ready" ? <video src={`${API_BASE}${st.url}`} autoPlay loop muted playsInline className="w-full h-full object-cover" data-testid={`clip-video-${c.key}`} />
                  : st?.status === "rendering" ? <Loader2 size={22} className="animate-spin text-sorted-slate" />
                  : <Play size={22} className="text-sorted-slate" />}
              </div>
              <p className="text-[11px] font-bold text-sorted-ink mt-1">{c.label}</p>
              <p className="text-[10px] text-sorted-slate capitalize" data-testid={`clip-status-${c.key}`}>{st?.status || "not made"}</p>
              {st?.error && <p className="text-[10px] text-sorted-rose">{st.error}</p>}
              <button onClick={() => render(c.key, st?.status === "ready")} disabled={st?.status === "rendering"} data-testid={`clip-render-${c.key}`}
                className="mt-1 text-[11px] font-bold text-sorted-rose flex items-center gap-1 mx-auto disabled:opacity-50"><RefreshCw size={11} strokeWidth={2.5} /> {st?.status === "ready" ? "Redo" : "Render"}</button>
            </div>
          );
        })}
      </div>
    </div>
  );
}
